import { HttpClient } from '@angular/common/http';
import { EventEmitter, Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { fromEvent, Observable, Subscription, timer } from 'rxjs';
import { debounceTime, delay, retryWhen, startWith, switchMap, tap } from 'rxjs/operators';
import * as _ from 'lodash';
import { BaseService } from '../services/misc/base.service';
import { ConfigService } from '../configuration/services/config.service';
import { LoggingService } from '../logging/logging.service';
import { NetworkConnectionServiceOptions, NetworkConnectionServiceOptionsToken } from './network.connection.options';
import { NetworkConnectionState } from './network.connection.state';
import { PITEventService } from '../event/pit.event.service';

@Injectable({
    providedIn: 'root'
})
export class NetworkConnectionService extends BaseService implements OnDestroy {
    private static DEFAULT_OPTIONS: NetworkConnectionServiceOptions = {
        enableHeartbeat: false,
        heartbeatUrl: '//internethealthtest.org',
        heartbeatInterval: 30000,
        heartbeatRetryInterval: 1000,
        requestMethod: 'head'
    };

    private stateChangeEventEmitter = new EventEmitter<NetworkConnectionState>();

    private currentState: NetworkConnectionState = {
        hasInternetAccess: false,
        hasNetworkConnection: window.navigator.onLine
    };
    private offlineSubscription: Subscription;
    private onlineSubscription: Subscription;
    private httpSubscription: Subscription;
    private serviceOptions: NetworkConnectionServiceOptions;

    /**
     * Current ConnectionService options. Notice that changing values of the returned object has not effect on service execution.
     * You should use "updateOptions" function.
     */
    public get options(): NetworkConnectionServiceOptions {
        return _.clone(this.serviceOptions);
    }

    constructor(configService: ConfigService, loggingService: LoggingService, private eventService: PITEventService, private http: HttpClient, @Inject(NetworkConnectionServiceOptionsToken) @Optional() options: NetworkConnectionServiceOptions) {
        super(configService, loggingService);

        this.serviceOptions = _.defaults({}, options, NetworkConnectionService.DEFAULT_OPTIONS);

        this.checkNetworkState();
        this.checkInternetState();
    }

    public refreshState(): void {
        this.emitEvent();
    }

    private checkInternetState() {

        if (!_.isNil(this.httpSubscription)) {
            this.httpSubscription.unsubscribe();
        }

        if (this.serviceOptions.enableHeartbeat) {
            this.httpSubscription = timer(0, this.serviceOptions.heartbeatInterval)
                .pipe(
                    switchMap(() => this.http[this.serviceOptions.requestMethod](this.serviceOptions.heartbeatUrl, { responseType: 'text' })),
                    retryWhen(errors =>
                        errors.pipe(
                            // log error message
                            tap(val => {
                                this.currentState.hasInternetAccess = false;
                                this.emitEvent();
                            }),
                            // restart after 5 seconds
                            delay(this.serviceOptions.heartbeatRetryInterval)
                        )
                    )
                )
                .subscribe(result => {
                    this.currentState.hasInternetAccess = true;
                    this.emitEvent();
                });
        } else {
            this.currentState.hasInternetAccess = false;
            this.emitEvent();
        }
    }

    private checkNetworkState() {
        this.onlineSubscription = fromEvent(window, 'online').subscribe(() => {
            this.currentState.hasNetworkConnection = true;
            this.checkInternetState();
            this.emitEvent();
        });

        this.offlineSubscription = fromEvent(window, 'offline').subscribe(() => {
            this.currentState.hasNetworkConnection = false;
            this.checkInternetState();
            this.emitEvent();
        });
    }

    private emitEvent() {
        this.stateChangeEventEmitter.emit(this.currentState);
        this.eventService.sendEvent('network', {
            type: 'network',
            state: this.currentState.hasNetworkConnection
        })
    }

    ngOnDestroy(): void {
        try {
            this.offlineSubscription.unsubscribe();
            this.onlineSubscription.unsubscribe();
            this.httpSubscription.unsubscribe();
        } catch (e) {
        }
    }

    /**
     * Monitor Network & Internet connection status by subscribing to this observer. If you set "reportCurrentState" to "false" then
     * function will not report current status of the connections when initially subscribed.
     * @param reportCurrentState Report current state when initial subscription. Default is "true"
     */
    public monitor(reportCurrentState = true): Observable<NetworkConnectionState> {
        return reportCurrentState ?
            this.stateChangeEventEmitter.pipe(
                debounceTime(300),
                startWith(this.currentState),
            )
            :
            this.stateChangeEventEmitter.pipe(
                debounceTime(300)
            );
    }

    /**
     * Update options of the service. You could specify partial options object. Values that are not specified will use default / previous
     * option values.
     * @param options Partial option values.
     */
    public updateOptions(options: Partial<NetworkConnectionServiceOptions>) {
        this.serviceOptions = _.defaults({}, options, this.serviceOptions);
        this.checkInternetState();
    }

}
